import type { ConversationMessage, MiraRouteId } from '@mira/clients-shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createWebApiClient, tokenStore } from '../../api/webSession.js';
import { hrefForRoute, navigateTo, replaceRoute } from '../../app/router.js';
import { showToast } from '../../app/toast.js';
import { webReferenceAssets } from '../../assets/catalog.js';
import { ChatComposer } from '../../ui/domain/index.js';
import { Avatar, Button, ErrorState, IconGlyph, MiraIcon } from '../../ui/primitives/index.js';
import { sourceBackHref } from '../../shell/SourceBack.js';
import {
  ConversationSourceComposer,
  MessageSourceMaterials,
  useConversationSourceMaterials,
} from '../../source-materials/ConversationSourceMaterials.js';
import { sampleMessages } from '../reference-preview-data.js';

const {
  avatarDad,
  avatarEmma,
  avatarMichael,
  avatarMira,
} = webReferenceAssets;

const autoStartConversations = new Map<string, Promise<{ id: string }>>();

export function ChatScreen(props: { autoStart?: boolean; conversationId: string; draft?: string | null; preview?: boolean; relationshipId?: string | null; returnTo?: string | null; screenId?: string; source?: MiraRouteId }) {
  const chat = useChatSession(props);
  const sources = useConversationSourceMaterials(Boolean(props.preview));
  const relationshipName = useRelationshipName(props.relationshipId, Boolean(props.preview));
  const previewMode = props.preview ? props.screenId || '11_chat' : '';
  const backHref = sourceBackHref('chat', props.source, props.returnTo || previewReturnTo(previewMode));
  const messages = props.preview ? previewChatMessages(previewMode) : chat.messages;
  const previewBusy = props.preview && ['chat_02_sending_message', 'chat_03_streaming_reply', 'ce_01_streaming_back', 'ce_03_return_partial_kept', 'ce_05_retry_partial'].includes(previewMode);
  const previewReplyFailed = props.preview && ['chat_05_mira_reply_failed_retry', 'ce_04_partial_failed'].includes(previewMode);
  const sourceLabel = chatSourceLabel(props.source, relationshipName, Boolean(props.relationshipId));
  const showSourceIntro = (props.preview && isPreviewNewConversation(previewMode)) || (!props.preview && props.conversationId === 'new' && messages.length === 0);
  const showStarterBubble = showSourceIntro && messages.length === 0 && !['chat_03_streaming_reply', 'ce_01_streaming_back', 'ce_03_return_partial_kept'].includes(previewMode);

  return (
    <article className="mira-chat-screen" data-preview-card={previewMode || undefined}>
      <header className="mira-chat-topbar">
        <div className="mira-chat-statusbar" aria-hidden="true"><strong>9:41</strong><span>▮▮▮  Wi‑Fi  ▰</span></div>
        <div className="mira-chat-navrow">
          <a href={backHref} aria-label="Back"><MiraIcon name="action.back" /></a>
          <div>
            <h1>{props.relationshipId ? 'Relationship conversation' : 'Chat with Mira'}</h1>
            <p>{sourceLabel}</p>
          </div>
          <a href={hrefForRoute('privacy', {}, { source: 'chat' })} aria-label="Privacy"><MiraIcon name="semantic.insight" /></a>
        </div>
      </header>
      <div className="mira-chat-thread">
        {showSourceIntro ? <ChatSourceCard sourceLabel={sourceLabel} /> : null}
        {showStarterBubble ? <ChatStarterBubble /> : null}
        {messages.map((message) => <ChatVisualMessage key={message.id} message={message} onSourceDeleted={chat.sourceDeleted} />)}
        {props.preview && ['chat_03_streaming_reply', 'ce_01_streaming_back', 'ce_03_return_partial_kept'].includes(previewMode) ? <ChatTypingBubble text="I hear two needs at once here: the need for stability, and the need to leave room to grow. We can hold both of them together." /> : null}
        {props.preview && previewMode === 'ce_05_retry_partial' ? <ChatTypingBubble /> : null}
        {chat.stream ? <ChatTypingBubble text={chat.stream} /> : (chat.awaitingResponse || chat.connecting) && !props.preview ? <ChatTypingBubble /> : null}
        {props.preview && ['chat_08_relationship_suggestion', '12_chat_relationship_suggestion'].includes(previewMode) ? <ChatRelationshipSuggestion /> : null}
        {props.preview && ['chat_09_relationship_alias_choice', '13_chat_relationship_alias'].includes(previewMode) ? <ChatAliasChoice /> : null}
        {previewReplyFailed ? <ErrorState title="Mira did not finish that reply" body="What you can see has been kept. Retry when you're ready." /> : null}
        {props.preview && previewReplyFailed ? <Button tone="secondary" onClick={() => showToast('Retrying…')}>Retry reply</Button> : null}
        {chat.error ? <ErrorState title="Conversation failed" body={chat.error} /> : null}
        {!props.preview && chat.canRetryAssistant ? <Button tone="secondary" onClick={chat.retryAssistant}>Retry reply</Button> : null}
      </div>
      <footer className="mira-chat-footer">
        {!props.preview ? <ConversationSourceComposer controller={sources} disabled={!chat.ready || chat.awaitingResponse} /> : null}
        <ChatComposer
          busy={previewBusy || (!props.preview && chat.awaitingResponse)}
          className="mira-chat-visual-composer"
          disabled={previewBusy || !chat.ready || chat.awaitingResponse}
          initialValue={props.autoStart && !props.preview ? '' : props.draft || previewDraft(previewMode)}
          onSend={(value) => {
            return chat.send(value, sources.readyIds).then((sent) => {
              if (sent) sources.consumeReady();
              return sent;
            });
          }}
          placeholder={sources.items.length ? 'Tell me one more thing about how you want me to see this…' : 'Write to Mira…'}
          submitAriaLabel={sources.blocksSend ? 'Finish processing sources first' : 'Send'}
          submitDisabled={sources.blocksSend}
          submitLabel={<MiraIcon name="action.send" />}
        />
      </footer>
    </article>
  );
}

type ChatDisplayMessage = ConversationMessage & { failed?: boolean; time?: string };

function previewChatMessages(mode: string): ChatDisplayMessage[] {
  if (['chat_01_ready_from_today', 'chat_06_return_to_topic_source', 'chat_07_return_to_relationship_source', 'gb_01_chat_from_today', 'gb_03_chat_from_topic'].includes(mode)) {
    return [];
  }
  if (['chat_02_sending_message', 'chat_03_streaming_reply', 'ce_01_streaming_back', 'ce_03_return_partial_kept'].includes(mode)) {
    return [
      { id: 'new-user-1', role: 'user', content: 'I keep replaying yesterday’s argument and I don’t know why it hurt so much.', time: mode === 'chat_02_sending_message' ? 'Sending...' : '10:30 AM' },
    ];
  }
  if (mode === 'chat_09_relationship_alias_choice' || mode === '13_chat_relationship_alias') {
    return [
      { id: 'alias-user', role: 'user', content: 'I talked to Alex about it yesterday.', time: '10:38 AM' },
      { id: 'alias-mira', role: 'assistant', content: 'Which Alex do you mean?', time: '10:38 AM' },
    ];
  }
  if (mode === 'chat_08_relationship_suggestion' || mode === '12_chat_relationship_suggestion') {
    return [
      { id: 'suggest-user', role: 'user', content: 'My mom thinks I should just stay in a stable job.', time: '10:13 AM' },
      { id: 'suggest-mira', role: 'assistant', content: 'It sounds like her opinion matters to you.', time: '10:13 AM' },
    ];
  }
  if (mode === 'chat_02_sending_message') {
    return [
      { id: 'sending-user-1', role: 'user', content: 'I keep thinking about leaving my job, but I’m scared of the uncertainty.', time: '10:30 AM' },
      { id: 'sending-user-2', role: 'user', content: 'Can you help me sort out what is fear and what is real caution?', time: 'Sending...' },
    ];
  }
  if (mode === 'chat_05_mira_reply_failed_retry' || mode === 'ce_04_partial_failed') {
    return [
      { id: 'reply-failed-user', role: 'user', content: 'Can we keep going from what you were saying?', time: '10:35 AM' },
      { id: 'reply-failed-mira', role: 'assistant', content: 'Yes. The part that feels important is that this is not only about work. It also touches...', time: 'Interrupted' },
    ];
  }
  if (mode === 'ce_06_retry_completed') {
    return [
      { id: 'retry-user', role: 'user', content: 'Can we keep going from what you were saying?', time: '10:35 AM' },
      { id: 'retry-mira', role: 'assistant', content: 'Yes. Let’s separate real-world caution from the fear that keeps you frozen. Both deserve respect, but they ask for different next steps.', time: '10:36 AM' },
    ];
  }
  return [
    { id: 'chat-user-1', role: 'user', content: 'I keep thinking about leaving my job, but I’m scared of the uncertainty.', time: '10:30 AM' },
    { id: 'chat-mira-1', role: 'assistant', content: 'It makes sense to feel that way. You’ve built a lot over the years, and the unknown can feel risky. Maybe we can explore what you’re really wanting more of, and what you’re hoping to move away from.', time: '10:30 AM' },
    { id: 'chat-user-2', role: 'user', content: 'My mom thinks I should just stay in a stable job.', time: '10:32 AM' },
    { id: 'chat-mira-2', role: 'assistant', content: 'It sounds like her opinion matters to you.', time: '10:32 AM' },
    { id: 'chat-user-3', role: 'user', content: 'Yes, but I also want to do what feels right for me.', failed: true },
  ];
}

function previewReturnTo(mode: string): string | null {
  if (mode === 'gb_03_chat_from_topic' || mode === 'chat_06_return_to_topic_source') return '/topics/communication';
  if (mode === 'chat_07_return_to_relationship_source') return '/relationships/emma';
  return null;
}

function isPreviewNewConversation(mode: string): boolean {
  return [
    'chat_01_ready_from_today',
    'chat_02_sending_message',
    'chat_03_streaming_reply',
    'chat_06_return_to_topic_source',
    'chat_07_return_to_relationship_source',
    'ce_01_streaming_back',
    'ce_03_return_partial_kept',
    'gb_01_chat_from_today',
    'gb_03_chat_from_topic',
  ].includes(mode);
}

function previewDraft(mode: string): string {
  if (['chat_01_ready_from_today', 'gb_01_chat_from_today', 'gb_03_chat_from_topic'].includes(mode)) {
    return "I keep replaying yesterday's argument and can't explain why it hurt so much.";
  }
  return '';
}

function chatSourceLabel(source: MiraRouteId | undefined, relationshipName: string, relationshipScoped: boolean): string {
  if (relationshipScoped) return relationshipName ? `From your relationship with ${relationshipName}` : 'From a relationship';
  if (source === 'topic-detail' || source === 'topics') return 'From a topic';
  if (source === 'relationship-detail' || source === 'relationships') return 'From relationships';
  if (source === 'memory') return 'From memory & insights';
  return 'From Today';
}

function ChatSourceCard(props: { sourceLabel: string }) {
  return (
    <section className="mira-chat-source-card">
      <span aria-hidden="true"><IconGlyph value="↩" /></span>
      <div>
        <h2>{props.sourceLabel}</h2>
        <p>Leaving neither archives nor deletes this conversation.</p>
      </div>
    </section>
  );
}

function ChatStarterBubble() {
  return (
    <section className="mira-chat-starter-bubble">
      <p>I'm here. Start with whatever holds the most of you right now.</p>
    </section>
  );
}

function ChatVisualMessage(props: { message: ChatDisplayMessage; onSourceDeleted?: (sourceId: string) => void }) {
  const role = props.message.role === 'user' ? 'user' : 'assistant';
  return (
    <article className="mira-chat-message" data-failed={props.message.failed || undefined} data-role={role}>
      {role === 'assistant' ? <Avatar decorative imageUrl={avatarMira} label="Mira" /> : null}
      <div>
        {role === 'assistant' ? <strong>Mira</strong> : null}
        <p>{props.message.content}</p>
        <MessageSourceMaterials sources={props.message.sourceMaterials} onDeleted={props.onSourceDeleted} />
        <span>{props.message.failed ? 'ⓘ Not sent — retry' : props.message.time || ''}</span>
      </div>
    </article>
  );
}

function ChatTypingBubble(props: { text?: string }) {
  return (
    <article className="mira-chat-message" data-role="assistant">
      <Avatar decorative imageUrl={avatarMira} label="Mira" />
      <div>
        <strong>Mira</strong>
        {props.text ? (
          <p>{props.text}</p>
        ) : (
          <p className="mira-chat-thinking" aria-label="Mira is responding">
            <i aria-hidden="true" />
            <i aria-hidden="true" />
            <i aria-hidden="true" />
          </p>
        )}
      </div>
    </article>
  );
}

function ChatRelationshipSuggestion() {
  return (
    <section className="mira-chat-suggestion-card">
      <div className="mira-chat-card-icon"><span aria-hidden="true"><IconGlyph value="♡" /></span></div>
      <div>
        <h2>Add this person to your relationships?</h2>
        <p>Candidate relationships only appear after real conversations form a traceable suggestion.</p>
        <small><IconGlyph value="⌑" /> Mom</small>
      </div>
      <button onClick={() => navigateTo('relationship-new', {}, { displayName: 'Mom' })} type="button">Add to relationships</button>
      <button onClick={() => showToast('Suggestion dismissed for now.')} type="button">Not now</button>
      <a href={hrefForRoute('chat', { conversationId: 'new' })}>Don't ask again</a>
    </section>
  );
}

function ChatAliasChoice() {
  const choices = [
    ['Alex Johnson', 'Close Friend', avatarMichael],
    ['Alex Chen', 'Former Colleague', avatarDad],
    ['Alex Ciccini', 'Family', avatarEmma],
  ] as const;
  return (
    <section className="mira-chat-alias-card">
      <header><span aria-hidden="true"><MiraIcon name="semantic.insight" /></span><div><h2>Who do you mean?</h2><p>Pick the person you just mentioned.</p></div></header>
      {choices.map(([name, relation, avatar]) => (
        <button key={name} onClick={() => showToast(`Selected ${name}.`)} type="button"><Avatar decorative imageUrl={avatar} label={name} /><strong>{name}</strong><small>{relation}</small></button>
      ))}
      <button onClick={() => navigateTo('relationship-new', {}, { displayName: 'Alex' })} type="button"><span aria-hidden="true"><IconGlyph value="＋" /></span><strong>New relationship</strong></button>
      <button onClick={() => showToast('You can skip this and keep talking.')} type="button"><span aria-hidden="true"><MiraIcon name="semantic.insight" /></span><strong>Not sure</strong></button>
    </section>
  );
}

function useRelationshipName(relationshipId?: string | null, preview = false): string {
  const [name, setName] = useState(preview && relationshipId ? 'Emma' : '');
  useEffect(() => {
    let active = true;
    if (!relationshipId || preview) return () => { active = false; };
    createWebApiClient().relationship(relationshipId)
      .then((detail) => {
        if (active) setName(detail.relationship.display_name);
      })
      .catch(() => undefined);
    return () => { active = false; };
  }, [relationshipId, preview]);
  return name;
}

function useChatSession(props: { autoStart?: boolean; conversationId: string; draft?: string | null; preview?: boolean; relationshipId?: string | null; returnTo?: string | null; source?: MiraRouteId }) {
  const previewMessages = useMemo(() => sampleMessages, []);
  const [activeConversationId, setActiveConversationId] = useState(props.preview ? props.conversationId : '');
  const [messages, setMessages] = useState<ConversationMessage[]>(props.preview ? previewMessages : []);
  const messagesRef = useRef<ConversationMessage[]>(props.preview ? previewMessages : []);
  const [stream, setStream] = useState('');
  const [error, setError] = useState('');
  const [connecting, setConnecting] = useState(!props.preview);
  const [awaitingResponse, setAwaitingResponse] = useState(false);
  const [responseFailed, setResponseFailed] = useState(false);
  const socketRef = useRef<WebSocket | null>(null);
  const assistantPollKeyRef = useRef('');
  const recoveryPollKeyRef = useRef('');

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    let active = true;
    if (props.preview) {
      setMessages(previewMessages);
      setConnecting(false);
      return () => {
        active = false;
      };
    }
    setConnecting(true);
    setError('');
    const api = createWebApiClient();
    void (async () => {
      try {
        await Promise.resolve();
        if (!active) return;
        if (props.conversationId === 'new') {
          const starterText = props.autoStart ? props.draft?.trim() || '' : '';
          if (!starterText) {
            setActiveConversationId('');
            setMessages([]);
            return;
          }
          setAwaitingResponse(true);
          setResponseFailed(false);
          const sessionToken = await tokenStore.getToken();
          const autoStartKey = `${sessionToken || 'guest'}:${props.relationshipId || 'free'}:${starterText}`;
          let conversationRequest = autoStartConversations.get(autoStartKey);
          if (!conversationRequest) {
            conversationRequest = api.createConversation({
              scope: conversationScope(props.relationshipId),
              starterText,
            }).then((response) => response.conversation);
            autoStartConversations.set(autoStartKey, conversationRequest);
            conversationRequest.catch(() => autoStartConversations.delete(autoStartKey));
            // ponytail: burst dedupe for dev/remount autostarts; server idempotency if cross-device repeats matter.
            window.setTimeout(() => autoStartConversations.delete(autoStartKey), 5000);
          }
          const conversation = await conversationRequest;
          if (!active) return;
          setActiveConversationId(conversation.id);
          const pageData = await api.conversationMessages(conversation.id, { limit: 80 });
          if (!active) return;
          setMessages((current) => mergeServerMessages(current, pageData.items));
          startAssistantPoll(conversation.id, 0);
          stabilizeConversationRoute(conversation.id, props);
          return;
        }
        const conversation = { id: props.conversationId };
        if (!active) return;
        setActiveConversationId(conversation.id);
        const pageData = await api.conversationMessages(conversation.id, { limit: 80 });
        if (!active) return;
        setMessages(pageData.items);
      } catch (err) {
        if (!active) return;
        setAwaitingResponse(false);
        setError('This conversation could not be opened right now. Please try again.');
      } finally {
        if (active) setConnecting(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [props.preview, props.conversationId, props.relationshipId, props.autoStart, props.draft, previewMessages]);

  useEffect(() => {
    if (props.preview || !activeConversationId) return;
    let active = true;
    const api = createWebApiClient();
    void (async () => {
      const token = await tokenStore.getToken();
      if (!active || !token) return;
      const socket = new WebSocket(api.chatSocketUrl(activeConversationId, token));
      socketRef.current = socket;
      socket.onmessage = (event) => {
        const payload = parseSocketPayload(event.data);
        if (!payload) return;
        if (payload.type === 'server.message.delta') {
          setStream((current) => {
            const next = `${current}${payload.delta || ''}`;
            sessionStorage.setItem(streamKey(activeConversationId), next);
            return next;
          });
        } else if (payload.type === 'server.message.done') {
          setAwaitingResponse(false);
          setResponseFailed(false);
          setStream('');
          sessionStorage.removeItem(streamKey(activeConversationId));
          if (payload.content) {
            setMessages((current) => mergeServerMessage(current, assistantMessage(payload.content || '')));
          }
        } else if ((payload.type === 'server.message.queued' || payload.type === 'server.message.duplicate') && payload.message) {
          setMessages((current) => mergeServerMessage(current, payload.message!));
        } else if (payload.type === 'server.error') {
          setAwaitingResponse(false);
          setResponseFailed(true);
          setError('Mira did not finish that reply. You can retry.');
        }
      };
    })();
    return () => {
      active = false;
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [props.preview, activeConversationId]);

  useEffect(() => {
    if (!activeConversationId) return;
    setStream(sessionStorage.getItem(streamKey(activeConversationId)) || '');
  }, [activeConversationId]);

  useEffect(() => {
    if (!activeConversationId || !stream) return;
    if (messages.at(-1)?.role !== 'assistant') return;
    sessionStorage.removeItem(streamKey(activeConversationId));
    setStream('');
  }, [activeConversationId, messages, stream]);

  useEffect(() => {
    if (props.preview || !activeConversationId || !stream || awaitingResponse) return;
    if (messages.at(-1)?.role === 'assistant') return;
    const assistantCount = messages.filter((message) => message.role === 'assistant').length;
    const pollKey = `${activeConversationId}:${assistantCount}:${stream}`;
    if (recoveryPollKeyRef.current === pollKey) return;
    recoveryPollKeyRef.current = pollKey;
    setAwaitingResponse(true);
    startAssistantPoll(activeConversationId, assistantCount);
  }, [props.preview, activeConversationId, awaitingResponse, messages, stream]);

  useEffect(() => {
    if (props.preview || !activeConversationId || !awaitingResponse) return;
    startAssistantPoll(activeConversationId, messages.filter((message) => message.role === 'assistant').length);
  }, [props.preview, activeConversationId, awaitingResponse, messages]);

  async function send(content: string, sourceMaterialIds: string[] = []): Promise<boolean> {
    if (props.preview) return false;
    const trimmed = content.trim();
    if (!trimmed) return false;
    let conversationId = activeConversationId;
    setError('');
    setAwaitingResponse(true);
    setResponseFailed(false);
    const assistantCountBefore = messages.filter((message) => message.role === 'assistant').length;
    const clientMessageId = crypto.randomUUID();
    const localMessage = localUserMessage(clientMessageId, trimmed);
    setMessages((current) => current.some((message) => message.id === localMessage.id) ? current : [...current, localMessage]);
    if (!conversationId && props.conversationId === 'new') {
      try {
        const api = createWebApiClient();
        const conversation = (await api.createConversation({
          scope: conversationScope(props.relationshipId),
          starterText: sourceMaterialIds.length ? null : trimmed,
        })).conversation;
        conversationId = conversation.id;
        setActiveConversationId(conversationId);
        if (sourceMaterialIds.length) {
          const response = await api.sendMessage(conversationId, trimmed, { clientMessageId, sourceMaterialIds });
          if (response.message) setMessages((current) => mergeServerMessage(current, response.message!));
        }
        const pageData = await api.conversationMessages(conversationId, { limit: 80 });
        setMessages((current) => mergeServerMessages(current.filter((message) => message.id !== localMessage.id), pageData.items));
        startAssistantPoll(conversationId, assistantCountBefore);
        stabilizeConversationRoute(conversationId, props);
        return true;
      } catch (err) {
        setAwaitingResponse(false);
        setMessages((current) => current.filter((message) => message.id !== localMessage.id));
        setError('This message did not send. Please try again.');
        return false;
      }
    }
    if (!conversationId) {
      setAwaitingResponse(false);
      setMessages((current) => current.filter((message) => message.id !== localMessage.id));
      setError('The conversation is still loading. Please try again shortly.');
      return false;
    }
    const socket = socketRef.current;
    if (socket?.readyState === WebSocket.OPEN && sourceMaterialIds.length === 0) {
      socket.send(JSON.stringify({ type: 'client.message', content: trimmed, clientMessageId, sourceMaterialIds }));
      startAssistantPoll(conversationId, assistantCountBefore);
      return true;
    }
    try {
      const response = await createWebApiClient().sendMessage(conversationId, trimmed, { clientMessageId, sourceMaterialIds });
      if (response.message) {
        setMessages((current) => mergeServerMessage(current, response.message!));
      }
      startAssistantPoll(conversationId, assistantCountBefore);
      return true;
    } catch (err) {
      setAwaitingResponse(false);
      setMessages((current) => current.filter((message) => message.id !== localMessage.id));
      setError('This message did not send. Please try again.');
      return false;
    }
  }

  function startAssistantPoll(conversationId: string, assistantCountBefore: number) {
    const pollKey = `${conversationId}:${assistantCountBefore}`;
    if (assistantPollKeyRef.current === pollKey) return;
    assistantPollKeyRef.current = pollKey;
    void pollAssistant(conversationId, assistantCountBefore, pollKey);
  }

  async function pollAssistant(conversationId: string, assistantCountBefore: number, pollKey: string) {
    const api = createWebApiClient();
    const deadline = Date.now() + 180_000;
    try {
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 2500));
        const pageData = await api.conversationMessages(conversationId, { limit: 80 }).catch(() => null);
        if (!pageData) continue;
        const merged = mergeServerMessages(messagesRef.current, pageData.items);
        messagesRef.current = merged;
        setMessages(merged);
        if (merged.filter((message) => message.role === 'assistant').length > assistantCountBefore) {
          setAwaitingResponse(false);
          setResponseFailed(false);
          return;
        }
      }
      setAwaitingResponse(false);
      setResponseFailed(true);
    } finally {
      if (assistantPollKeyRef.current === pollKey) assistantPollKeyRef.current = '';
    }
  }

  async function retryAssistant() {
    if (!activeConversationId) {
      setError('The conversation is still loading. Please try again shortly.');
      return;
    }
    try {
      setError('');
      setConnecting(true);
      setAwaitingResponse(true);
      setResponseFailed(false);
      await createWebApiClient().retryAssistantResponse(activeConversationId);
      startAssistantPoll(activeConversationId, messages.filter((message) => message.role === 'assistant').length);
    } catch (err) {
      setAwaitingResponse(false);
      setResponseFailed(true);
      setError('The reply could not be retried right now. Please try again later.');
    } finally {
      setConnecting(false);
    }
  }

  function sourceDeleted(sourceId: string) {
    setMessages((current) => current.map((message) => ({
      ...message,
      sourceMaterials: message.sourceMaterials?.map((source) => source.id === sourceId
        ? { ...source, status: 'deleted', extractionMethod: null }
        : source),
    })));
  }

  return {
    canRetryAssistant: responseFailed && !stream && !awaitingResponse && messages.at(-1)?.role === 'user',
    connecting,
    error,
    awaitingResponse,
    messages,
    ready: props.preview || Boolean(activeConversationId) || props.conversationId === 'new',
    retryAssistant,
    send,
    sourceDeleted,
    stream,
  };
}

function stabilizeConversationRoute(
  conversationId: string,
  props: { conversationId: string; preview?: boolean; relationshipId?: string | null; returnTo?: string | null; source?: MiraRouteId },
) {
  if (props.preview || props.conversationId !== 'new') return;
  const query: Record<string, string> = {};
  if (props.source) query.source = props.source;
  if (props.relationshipId) query.relationshipId = props.relationshipId;
  if (props.returnTo) query.returnTo = props.returnTo;
  replaceRoute('chat', { conversationId }, query);
}

function localUserMessage(clientMessageId: string, content: string): ConversationMessage {
  return { id: `local-${clientMessageId}`, role: 'user', content, created_at: new Date().toISOString() };
}

function conversationScope(relationshipId?: string | null): { type: 'free' } | { type: 'relationship'; relationshipId: string } {
  return relationshipId ? { type: 'relationship', relationshipId } : { type: 'free' };
}

function mergeServerMessages(current: ConversationMessage[], incoming: ConversationMessage[]): ConversationMessage[] {
  return incoming.reduce((messages, message) => mergeServerMessage(messages, message), current);
}

function mergeServerMessage(current: ConversationMessage[], incoming: ConversationMessage): ConversationMessage[] {
  if (current.some((message) => message.id === incoming.id)) return current;
  if (incoming.role === 'user') {
    const localIndex = current.findIndex((message) => message.id.startsWith('local-') && message.role === 'user' && message.content === incoming.content);
    return localIndex === -1 ? [...current, incoming] : current.map((message, index) => index === localIndex ? incoming : message);
  }
  if (incoming.role === 'assistant') {
    const assistantIndex = current.findIndex((message) =>
      message.role === 'assistant'
      && message.content === incoming.content
      && (isTemporaryAssistant(message) || isTemporaryAssistant(incoming)));
    if (assistantIndex !== -1) {
      const currentMessage = current[assistantIndex];
      return currentMessage && isTemporaryAssistant(currentMessage) && !isTemporaryAssistant(incoming)
        ? current.map((message, index) => index === assistantIndex ? incoming : message)
        : current;
    }
  }
  return [...current, incoming];
}

function parseSocketPayload(value: unknown): { type?: string; delta?: string; content?: string; error?: string; message?: ConversationMessage } | null {
  try {
    return JSON.parse(String(value)) as { type?: string; delta?: string; content?: string; error?: string; message?: ConversationMessage };
  } catch {
    return null;
  }
}

function assistantMessage(content: string): ConversationMessage {
  return {
    id: `assistant-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    role: 'assistant',
    content,
  };
}

function isTemporaryAssistant(message: ConversationMessage): boolean {
  return message.id.startsWith('assistant-');
}

function streamKey(conversationId: string): string {
  return `mira.chat.partial.${conversationId}`;
}
